<template>
    <UserTrackingTable>
        <template #actions>
            <button
                v-if="showBulkRetryButton"
                class="bg-primary-accent-500 btn text-white"
                @click="retryCalls(selected)"
            >
                Retry
            </button>
        </template>
        <template #filters>
            <DropdownFilter
                @apply="applyFilters(false)"
                @clear="clearFilters"
                @disable="disableFilter"
            >
                <DropdownFilterItem>
                    <label class="flex items-center">
                        <SelectField
                            v-model="filtersFields.platform_name"
                            v-model:options="filterPlatformOptions"
                            name="platform_name"
                            class="w-full"
                            :placeholder="$t('platform')"
                            :on-search="loadPlatformNameOptions"
                            :disabled="disableFilter('platform_name')"
                        />
                    </label> </DropdownFilterItem
                ><DropdownFilterItem>
                    <label class="flex items-center">
                        <SelectField
                            v-model="filtersFields.status"
                            name="status"
                            class="w-full"
                            :options="statusOptions"
                            :disabled="disableFilter('status')"
                            :placeholder="$t('status')"
                        />
                    </label>
                </DropdownFilterItem>
            </DropdownFilter>
        </template>
    </UserTrackingTable>
    <ViewWebhookCallModal
        v-if="selectedWebhookCall"
        v-model:open="viewWebhookCallModalOpen"
        :webhook-call="(selectedWebhookCall as WebhookCall)"
    />
</template>

<script setup lang="ts">
import DropdownFilter from '@src/components/DropdownFilter.vue'
import DropdownFilterItem from '@src/components/DropdownFilterItem.vue'
import SelectField from '@src/components/SelectField.vue'
import { useTable } from '@src/composables/common'
import { Platform, TableSorting } from '@src/types'
import { getPlatforms } from '@src/utils/api/platforms'
import {
    getWebhookCalls,
    retryWebhookCalls,
    WebhookCall,
} from '@src/utils/api/webhookCalls'
import { useToastStore } from '@src/utils/stores/toast'
import moment from 'moment'
import { computed, onMounted, ref, toRefs } from 'vue'
import ViewWebhookCallModal from './ViewWebhookCallModal.vue'

const props = withDefaults(
    defineProps<{
        initialFilters?: {
            platform_name?: string | null
        } | null
    }>(),
    {
        initialFilters: null,
    }
)

const { initialFilters } = toRefs(props)

const toast = useToastStore()

const webhookCalls = ref<WebhookCall[]>([])
const webhookCallsCount = ref<number>(0)
const viewWebhookCallModalOpen = ref(false)
const selectedWebhookCall = ref<WebhookCall | null>(null)

const showBulkRetryButton = computed(() => {
    if (selected.value.length > 0) {
        const allFailed = selected.value.every((id) =>
            webhookCalls.value.some(
                (call) => call.id === id && call.status === 'failed'
            )
        )
        return allFailed
    }
    return false
})

const filterPlatformOptions = ref<{ label: string; value: string }[]>([])

const {
    TableWrapper: UserTrackingTable,
    sorting,
    page,
    limit,
    filters,
    filtersFields,
    applyFilters,
    clearFilters,
    setFilters,
    selected,
} = useTable({
    loadData: load,
    filters: {
        action: null,
        platform_name: null,
        status: null,
    },
    disableFilters: initialFilters.value
        ? Object.keys(initialFilters.value)
        : [],
    tableProps: {
        id: 'webhook-calls-table',
        label: 'webhook_calls',
        help: 'help_webhook_calls_table',
        pagination: true,
        data: webhookCalls,
        total: webhookCallsCount,
        sorting: ref<TableSorting[]>([
            {
                field: 'attempted_at',
                direction: 'desc',
            },
        ]),
        columns: [
            {
                label: 'ID',
                key: 'id',
                select: true,
            },
            {
                label: 'ID',
                key: 'id',
            },
            {
                label: 'action',
                key: 'action',
            },
            {
                label: 'platform_name',
                key: 'platform_name',
            },
            {
                label: 'status',
                key: 'status',
            },
            {
                label: 'attempt_number',
                key: 'attempt_number',
            },
            {
                label: 'last_attempted_at',
                key: 'attempted_at',
                cellRenderer: (props: { rowData: WebhookCall }) => {
                    return moment(props.rowData.attempted_at).format(
                        'DD.MM.yyyy HH:mm'
                    )
                },
            },
        ],
        actions: [
            {
                action: (rowData: WebhookCall) => {
                    selectedWebhookCall.value = rowData
                    viewWebhookCallModalOpen.value = true
                },
                icon: 'fa-eye',
                title: 'view',
                condition: true,
            },
            {
                action: async (rowData: WebhookCall) => {
                    await retryCalls([rowData.id])
                },
                icon: 'fa-solid fa-rotate-right',
                title: 'retry',
                condition: (rowData: WebhookCall) => {
                    return rowData.status === 'failed'
                },
            },
        ],
    },
})

const statusOptions = ref<{ label: string; value: string }[]>([
    {
        label: 'Success',
        value: 'success',
    },
    {
        label: 'Error',
        value: 'error',
    },
])

const disableFilter = (filter: string) => {
    if (initialFilters.value) {
        return (
            Object.keys(initialFilters.value).filter((item) => {
                return item === filter
            }).length > 0
        )
    }
    return false
}

const loadPlatformNameOptions = async (search: string) => {
    const platformsResponse = await getPlatforms({
        'filter[platform_name]': search,
    })
    filterPlatformOptions.value = platformsResponse.data.platforms.map(
        (platform: Platform) => {
            return {
                label: platform.platform_name as string,
                value: platform.platform_name as string,
            }
        }
    )
}

async function load() {
    const webhookCallsResponse = await getWebhookCalls(
        filters.value,
        page.value,
        limit.value,
        sorting.value
    )
    if (
        webhookCallsResponse.data.success &&
        webhookCallsResponse.status === 206
    ) {
        webhookCalls.value = webhookCallsResponse.data.webhook_calls
        webhookCallsCount.value = webhookCallsResponse.data.webhook_calls_count
    }
}

const retryCalls = async (ids: string[]) => {
    const response = await retryWebhookCalls(ids)
    if (response.status === 200) {
        toast.success(response.data.message)
    }
    await load()
}

onMounted(async () => {
    if (initialFilters.value) {
        filtersFields.value = {
            ...filtersFields.value,
            ...initialFilters.value,
        }
        setFilters()
    }
    await load()
})
</script>
